import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Bot, User, Loader2, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { ChatWelcome } from "@/components/ChatWelcome";

interface Message {
  role: "user" | "assistant";
  content: string;
}

// Clean markdown formatting from AI responses
const cleanMarkdown = (text: string): string => {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/(?<!\w)_([^_]+)_(?!\w)/g, '$1')
    .replace(/#{1,6}\s/g, '')
    .replace(/`{3}[\s\S]*?`{3}/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s*[-*+]\s/gm, '')
    .replace(/^\s*\d+\.\s/gm, '')
    .trim();
};

// Rate limit for guests: 3 messages per minute
const RATE_LIMIT_COUNT = 3;
const RATE_LIMIT_WINDOW_MS = 60000;

export default function GuestChat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messageTimestampsRef = useRef<number[]>([]);
  const lastSendTimeRef = useRef<number>(0);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check rate limit
  const checkRateLimit = useCallback((): boolean => {
    const now = Date.now();
    messageTimestampsRef.current = messageTimestampsRef.current.filter(
      (ts) => now - ts < RATE_LIMIT_WINDOW_MS
    );
    
    if (messageTimestampsRef.current.length >= RATE_LIMIT_COUNT) {
      const oldestTimestamp = messageTimestampsRef.current[0];
      const waitTime = Math.ceil((RATE_LIMIT_WINDOW_MS - (now - oldestTimestamp)) / 1000);
      toast({
        title: "Slow down!",
        description: `You can send ${RATE_LIMIT_COUNT} messages per minute. Try again in ${waitTime}s.`,
        variant: "destructive",
      });
      return false;
    }
    return true;
  }, [toast]);

  // Debounce check
  const canSend = useCallback((): boolean => {
    const now = Date.now();
    const DEBOUNCE_MS = 500;
    if (now - lastSendTimeRef.current < DEBOUNCE_MS) {
      return false;
    }
    lastSendTimeRef.current = now;
    return true;
  }, []);

  const streamChat = async (userMessage: string, retryCount = 0): Promise<boolean> => {
    const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`;
    
    try {
      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Use anon key for guest access
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ 
          messages: [{ role: "user", content: userMessage }],
          isGuest: true
        }),
      });

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const errorMessage = errorData.error || "Something went wrong";
        
        if (resp.status === 429 || resp.status === 402) {
          toast({
            title: "AI Paused",
            description: errorMessage,
            variant: "destructive",
          });
          return false;
        }
        
        if (retryCount === 0) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          return streamChat(userMessage, 1);
        }
        
        toast({
          title: "Connection Issue",
          description: "Couldn't reach the server. Check your connection and try again.",
          variant: "destructive",
        });
        return false;
      }

      if (!resp.body) {
        if (retryCount === 0) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          return streamChat(userMessage, 1);
        }
        throw new Error("No response body");
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let streamDone = false;
      let assistantSoFar = "";

      // Add empty assistant message
      setMessages(prev => [...prev, { role: "assistant", content: "" }]);

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantSoFar += content;
              setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage?.role === "assistant") {
                  lastMessage.content = assistantSoFar;
                }
                return newMessages;
              });
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }
      return true;
    } catch (error) {
      console.error("Stream error:", error);
      
      if (retryCount === 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return streamChat(userMessage, 1);
      }
      
      toast({
        title: "Connection Issue",
        description: "Couldn't reach the server. Check your connection and try again.",
        variant: "destructive",
      });
      setMessages(prev => prev.slice(0, -1));
      return false;
    }
  };

  const sendMessage = useCallback(async (messageText: string) => {
    const trimmedInput = messageText.trim();
    if (!trimmedInput || isLoading) return;

    if (!canSend()) return;
    if (!checkRateLimit()) return;

    messageTimestampsRef.current.push(Date.now());

    // Add user message
    const userMessage: Message = { role: "user", content: trimmedInput };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    const success = await streamChat(trimmedInput);
    
    if (!success) {
      setMessages(prev => prev.filter((_, i) => i !== prev.length - 1 || prev[prev.length - 1]?.role !== "user"));
    }
    
    setIsLoading(false);
  }, [isLoading, canSend, checkRateLimit]);

  const handleSend = useCallback(() => {
    sendMessage(input);
  }, [input, sendMessage]);

  const handleSuggestionClick = useCallback((message: string) => {
    sendMessage(message);
  }, [sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px";
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages area */}
      <div 
        className="flex-1 overflow-y-auto px-4 py-6"
        style={{ paddingBottom: isMobile ? '140px' : '120px' }}
      >
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <ChatWelcome isGuest={true} onSuggestionClick={handleSuggestionClick} />
          </div>
        ) : (
          <div className="max-w-2xl mx-auto space-y-5">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 ${
                  message.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                {message.role === "assistant" && (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Bot className="w-4 h-4 text-primary" />
                  </div>
                )}
                <div
                  className={`rounded-2xl px-4 py-3 max-w-[85%] ${
                    message.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-foreground"
                  }`}
                >
                  <p className="text-[16px] md:text-[15px] whitespace-pre-wrap leading-relaxed break-words">
                    {message.role === "assistant" ? cleanMarkdown(message.content) : message.content}
                  </p>
                </div>
                {message.role === "user" && (
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1">
                    <User className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}
              </div>
            ))}
            {isLoading && messages[messages.length - 1]?.role === "user" && (
              <div className="flex gap-3 justify-start">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <Bot className="w-4 h-4 text-primary" />
                </div>
                <div className="rounded-2xl px-4 py-3 bg-muted">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Fixed Input area */}
      <div 
        className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-background via-background to-transparent z-30"
        style={{
          paddingBottom: isMobile ? 'calc(16px + env(safe-area-inset-bottom))' : '16px',
          paddingTop: '24px'
        }}
      >
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-card rounded-2xl shadow-lg border border-border/50 p-2 flex gap-2 items-end">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder="Ask about taxes, deductions, receipts..."
              className="flex-1 min-h-[48px] max-h-[150px] resize-none text-[16px] bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none px-3 py-3 leading-normal placeholder:text-muted-foreground/50"
              disabled={isLoading}
              rows={1}
            />
            <Button 
              onClick={handleSend} 
              disabled={!input.trim() || isLoading}
              size="icon"
              className="h-12 w-12 shrink-0 rounded-xl"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-2">
            ✨ Try asking about home office deductions, meal expenses, or business travel
          </p>
        </div>
      </div>
    </div>
  );
}
